// src/components/wells/wellIntelUi.js — shared tokens for the Well Intelligence components
import React, { memo, useMemo } from 'react';

export const TIER_META = Object.freeze({
  critical: { label: 'Critical', scheme: 'red', color: 'red.500' },
  high: { label: 'High', scheme: 'orange', color: 'orange.400' },
  watch: { label: 'Watch', scheme: 'yellow', color: 'yellow.500' },
  stable: { label: 'Stable', scheme: 'green', color: 'green.500' },
  unknown: { label: 'No data', scheme: 'gray', color: 'gray.400' },
});
export const tierMeta = (t) => TIER_META[t] || TIER_META.unknown;

export const PRIORITY_SCHEME = { urgent: 'red', high: 'orange', medium: 'yellow', low: 'gray' };

export const INSPECTION_META = Object.freeze({
  overdue: { label: 'Overdue', scheme: 'red' },
  never_inspected: { label: 'Never inspected', scheme: 'purple' },
  due_soon: { label: 'Due soon', scheme: 'orange' },
  current: { label: 'Current', scheme: 'green' },
});

// "Drainage Well", "drainage_well", "Storm Well" … belong to the well family (same rule as the backend)
export const WELL_TYPE_RE = /^(well|drainage[ _-]?well|storm[ _-]?well|soak[ _-]?away[ _-]?well|disposal[ _-]?well|recharge[ _-]?well)$/i;
// --- UPDATE (wells v1.3): any type ending in "well"/"wells" ("Drainage Wells", "Storm Drain Well") — same rule as the backend
export const isWellType = (type) => {
  const t = String(type || '').trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  return WELL_TYPE_RE.test(t) || /(^|\s)wells?$/.test(t);
};
export const DRAIN_ROLE_META = Object.freeze({
  catch_basin: { label: 'Catch basin', scheme: 'teal' },
  drainage_well: { label: 'Drainage well', scheme: 'cyan' },
  drainage: { label: 'Drainage', scheme: 'blue' },
});
export const drainRoleMeta = (r) => DRAIN_ROLE_META[r] || DRAIN_ROLE_META.drainage;
// --- END UPDATE
export const wellKindLabel = (w) => (w?.well_kind === 'drainage' ? 'Drainage well' : 'Well');

export const AI_GRADIENT = 'linear(to-r, purple.500, blue.500, cyan.400)';

export const formatAgo = (ts, now = Date.now()) => {
  const t = ts ? new Date(ts).getTime() : NaN;
  if (!Number.isFinite(t)) return null;
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

/** 0–100 series sparkline (colour = currentColor). */
export const LevelSparkline = memo(({ values = [], width = 120, height = 28 }) => {
  const d = useMemo(() => {
    if (!Array.isArray(values) || values.length < 2) return null;
    const step = (width - 4) / (values.length - 1);
    return values.map((v, i) => `${i ? 'L' : 'M'}${(2 + i * step).toFixed(1)},${(height - 2 - (Math.min(100, Math.max(0, v)) / 100) * (height - 4)).toFixed(1)}`).join(' ');
  }, [values, width, height]);
  if (!d) return null;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Water level trend, last 24 hours" style={{ display: 'block' }}>
      <line x1="0" x2={width} y1={height - 2 - 0.9 * (height - 4)} y2={height - 2 - 0.9 * (height - 4)} stroke="currentColor" strokeOpacity="0.3" strokeDasharray="2 2" />
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
});
LevelSparkline.displayName = 'LevelSparkline';
